import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Avatar,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Paper,
  Rating,
  Fab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  useTheme,
  FormControlLabel,
  Switch,
  Tooltip,
  Stack,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Person,
  Email,
  Star,
  Close,
  RoomService,
  Lock,
  ConfirmationNumber,
  AccountCircle,
} from '@mui/icons-material';
import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { ConfirmationModal, AlertModal } from '../components/common/ConfirmationModal';
import { CustomSnackbar } from '../components/common/CustomSnackbar';
import { useConfirmation } from '../hooks/useConfirmation';
import { useAuth } from '../contexts/AuthContext';
import { GET_ALL_STAFF, GET_ALL_SERVICES, GET_STORE_BY_ID } from '../graphql/queries';
import { CREATE_STAFF, UPDATE_STAFF, DELETE_STAFF } from '../graphql/mutations';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { STAFF_ROLES, StaffFormData, StaffRole, getRoleDisplayName, getRoleColor, getStaffRoleForStore } from '../types/staff';

const Staff = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { storeId: paramStoreId } = useParams();
  const { storeId: authStoreId } = useAuth();
  const storeId = paramStoreId || authStoreId;
  // Modal shown when CreateStaff/UpdateStaff returns SEAT_LIMIT_EXCEEDED.
  const [seatLimitModalOpen, setSeatLimitModalOpen] = useState(false);

  // Custom confirmation hook
  const {
    confirmationState,
    alertState,
    snackbarState,
    confirmDelete,
    closeConfirmation,
    closeAlert,
    handleConfirm,
    showSuccessSnackbar,
    showErrorSnackbar,
    closeSnackbar,
  } = useConfirmation();

  // Update localStorage when storeId from URL changes
  useEffect(() => {
    if (paramStoreId) {
      localStorage.setItem('storeId', paramStoreId);
    }
  }, [paramStoreId]);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingStaff, setEditingStaff] = useState<any>(null);
  const [formData, setFormData] = useState<StaffFormData>({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    password: '',
    services: [],
    role: STAFF_ROLES.STAFF,
    title: '',
    isActive: true,
  });

  const { data: staffData, loading: staffLoading, refetch, error } = useQuery(
    GET_ALL_STAFF,
    {
      variables: { storeId },
      skip: !storeId,
    }
  );

  const { data: servicesData, loading: servicesLoading } = useQuery(
    GET_ALL_SERVICES,
    {
      variables: { storeId },
      skip: !storeId,
    }
  );

  // Pull seat counters off the store record. Drives the "X / Y seats used"
  // badge and the at-limit Add-staff button state. Refetched whenever the
  // CreateStaff/UpdateStaff mutations succeed, so the counters stay accurate.
  const { data: storeData, refetch: refetchStore } = useQuery(GET_STORE_BY_ID, {
    variables: { storeId },
    skip: !storeId,
    fetchPolicy: 'cache-and-network',
  });
  const seatCount: number = storeData?.storeDetails?.seatCount ?? 2;
  const activeSeatsUsed: number = storeData?.storeDetails?.activeSeatsUsed ?? 0;
  const atSeatLimit = activeSeatsUsed >= seatCount;

  const [createStaff] = useMutation(CREATE_STAFF);
  const [updateStaff] = useMutation(UPDATE_STAFF);
  const [deleteStaff] = useMutation(DELETE_STAFF);

  const staff = staffData?.allStaff || [];
  const services = servicesData?.allServices || [];

  const handleOpenDialog = (staffMember?: any) => {
    if (staffMember) {
      setEditingStaff(staffMember);
      const currentRole = storeId ? getStaffRoleForStore(staffMember, storeId) : STAFF_ROLES.STAFF;
      // Get isActive from the staff store relationship for this store
      const staffStore = staffMember.staffStores?.find((ss: any) => ss.store?.id === storeId);
      const isActive = staffStore?.isActive ?? true;
      
      setFormData({
        firstName: staffMember.firstName || '',
        lastName: staffMember.lastName || '',
        username: staffMember.username || '',
        email: staffMember.email || '',
        password: '',
        services: staffMember.services?.map((service: any) => service.id) || [],
        role: currentRole,
        title: staffStore?.title || '',
        isActive: isActive,
      });
    } else {
      setEditingStaff(null);
      setFormData({
        firstName: '',
        lastName: '',
        username: '',
        email: '',
        password: '',
        services: [],
        role: STAFF_ROLES.STAFF,
        title: '',
        isActive: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingStaff(null);
  };

  // Backend returns errors like "SEAT_LIMIT_EXCEEDED:<store_id>" when a
  // staff mutation would push active seats above the cap. We detect that
  // structured prefix and route to the buy-seats modal instead of the
  // generic error snackbar.
  const isSeatLimitError = (errors: string[] | undefined): boolean =>
    !!errors?.some((e) => typeof e === 'string' && e.startsWith('SEAT_LIMIT_EXCEEDED'));

  const handleSubmit = async () => {
    try {
      if (editingStaff) {
        const result = await updateStaff({
          variables: {
            staffId: editingStaff.id,
            ...formData,
            storeIds: [storeId], // Include storeIds to update the role
          },
        });

        if (result.data?.updateStaff?.ok) {
          showSuccessSnackbar('Staff member updated successfully!');
          refetch();
          refetchStore();
          handleCloseDialog();
        } else {
          const errors = result.data?.updateStaff?.errors || ['Unknown error occurred'];
          if (isSeatLimitError(errors)) {
            handleCloseDialog();
            setSeatLimitModalOpen(true);
          } else {
            showErrorSnackbar(errors.join(', '));
          }
        }
      } else {
        const result = await createStaff({
          variables: {
            ...formData,
            sendInviteEmail: true,
            storeIds: [storeId],
          },
        });

        if (result.data?.createStaff?.ok) {
          showSuccessSnackbar('Staff member created successfully!');
          refetch();
          refetchStore();
          handleCloseDialog();
        } else {
          const errors = result.data?.createStaff?.errors || ['Unknown error occurred'];
          if (isSeatLimitError(errors)) {
            handleCloseDialog();
            setSeatLimitModalOpen(true);
          } else {
            showErrorSnackbar(errors.join(', '));
          }
        }
      }
    } catch (error) {
      console.error('Error saving staff:', error);
      showErrorSnackbar('Failed to save staff member. Please try again.');
    }
  };

  const handleDelete = async (staffId: string) => {
    confirmDelete('this staff member', async () => {
      try {
        const result = await deleteStaff({
          variables: { staffId },
        });
        
        if (result.data?.deleteStaff?.ok) {
          refetch();
          showSuccessSnackbar('Staff member deleted successfully!');
        } else {
          const errors = result.data?.deleteStaff?.errors || ['Unknown error occurred'];
          showErrorSnackbar(errors.join(', '));
        }
      } catch (error) {
        console.error('Error deleting staff:', error);
        showErrorSnackbar('Failed to delete staff member. Please try again.');
      }
    });
  };

  if (staffLoading || servicesLoading) {
    return <LoadingSpinner message="Loading staff..." />;
  }

  if (!storeId) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          No store ID available. Please navigate from the stores page.
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          Error loading staff: {error.message}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: { xs: 2, md: 3 }, 
      maxWidth: '1200px', 
      mx: 'auto',
      minHeight: '100vh',
      bgcolor: 'background.default'
    }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', md: 'center' },
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2, md: 0 },
          mb: 3,
          pb: 2,
          borderBottom: 1,
          borderColor: 'divider'
        }}>
          <Box>
            <Typography variant="h4" component="h1" fontWeight={700} sx={{ mb: 1 }}>
              Staff Management
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
              Manage your barbershop team members and their permissions
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }}>
            {/* Seat usage badge — amber when at the cap, neutral otherwise */}
            <Chip
              icon={<ConfirmationNumber sx={{ fontSize: 16 }} />}
              label={`${activeSeatsUsed} / ${seatCount} seats used`}
              color={atSeatLimit ? 'warning' : 'default'}
              variant={atSeatLimit ? 'filled' : 'outlined'}
              sx={{ fontWeight: 600 }}
            />
            {atSeatLimit && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate(`/store/${storeId}/seats`)}
                sx={{ borderRadius: 2, fontWeight: 600 }}
              >
                Buy more seats
              </Button>
            )}
            <Tooltip title={atSeatLimit ? 'All seats in use — buy more to add another barber' : ''}>
              {/* span wrapper lets Tooltip work on a disabled button */}
              <span>
                <Button
                  variant="contained"
                  startIcon={atSeatLimit ? <Lock /> : <Add />}
                  onClick={() => handleOpenDialog()}
                  disabled={atSeatLimit}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1.5,
                    fontWeight: 600,
                    boxShadow: theme.shadows[2],
                    '&:hover': { boxShadow: theme.shadows[4] },
                  }}
                >
                  Add Staff Member
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Box>

        {staff.length === 0 ? (
          <Paper sx={{ 
            p: { xs: 3, md: 4 }, 
            textAlign: 'center',
            borderRadius: 2,
            boxShadow: theme.shadows[1],
            bgcolor: 'background.paper'
          }}>
            <Person sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom fontWeight={600}>
              No staff members yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Add your first team member to get started
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1.5,
                fontWeight: 600,
                boxShadow: theme.shadows[2],
              }}
            >
              Add Staff Member
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {staff.map((staffMember: any) => (
              <Grid item xs={12} md={6} lg={4} key={staffMember.id}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <Card sx={{ 
                    height: '100%',
                    borderRadius: 2,
                    boxShadow: theme.shadows[1],
                    bgcolor: 'background.paper',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      boxShadow: theme.shadows[4],
                      transform: 'translateY(-2px)',
                    }
                  }}>
                    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ 
                            width: 56, 
                            height: 56,
                            bgcolor: theme.palette.primary.main,
                            fontSize: '1.5rem',
                            fontWeight: 600
                          }}>
                            {staffMember.firstName?.[0] || staffMember.username?.[0] || 'U'}
                          </Avatar>
                          <Box>
                            <Typography variant="h6" component="h2" fontWeight={600} sx={{ mb: 0.5 }}>
                              {staffMember.fullName}
                            </Typography>
                            {(() => {
                              const staffStore = staffMember.staffStores?.find((ss: any) => ss.store?.id === storeId);
                              return staffStore?.title ? (
                                <Typography variant="body2" color="primary" fontWeight={500} sx={{ mb: 0.25 }}>
                                  {staffStore.title}
                                </Typography>
                              ) : null;
                            })()}
                            <Typography variant="body2" color="text.secondary">
                              @{staffMember.username}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {/* Admin override: jump into the barber's public-profile
                              editor with ?staff={id}. Reuses the same MyProfile
                              page component; backend permission decorator
                              enforces admin-at-shared-store. */}
                          <Tooltip title="Edit public profile (bio, portfolio, slug)">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/my-profile?staff=${staffMember.id}`)}
                              sx={{
                                color: 'text.secondary',
                                '&:hover': {
                                  color: theme.palette.primary.main,
                                  bgcolor: theme.palette.primary.main + '10',
                                }
                              }}
                            >
                              <AccountCircle />
                            </IconButton>
                          </Tooltip>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(staffMember)}
                            sx={{
                              color: 'text.secondary',
                              '&:hover': {
                                color: theme.palette.primary.main,
                                bgcolor: theme.palette.primary.main + '10',
                              }
                            }}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(staffMember.id)}
                            sx={{
                              color: 'text.secondary',
                              '&:hover': {
                                color: theme.palette.error.main,
                                bgcolor: theme.palette.error.main + '10',
                              }
                            }}
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      </Box>

                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: 1.5, 
                        mb: 2,
                        p: 2,
                        bgcolor: 'background.default',
                        borderRadius: 1.5,
                        border: 1,
                        borderColor: 'divider'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              p: 0.5,
                              borderRadius: 1,
                              backgroundColor: theme.palette.info.main,
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minWidth: 24,
                              height: 24
                            }}
                          >
                            <Email fontSize="small" />
                          </Box>
                          <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                            {staffMember.email}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              p: 0.5,
                              borderRadius: 1,
                              backgroundColor: theme.palette.warning.main,
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minWidth: 24,
                              height: 24
                            }}
                          >
                            <Star fontSize="small" />
                          </Box>
                          <Rating value={Number(staffMember.rating) || 0} precision={0.1} size="small" readOnly />
                          <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                            ({Number(staffMember.rating || 0).toFixed(1)})
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                        {(() => {
                          // Get isActive status from the staff store relationship for this store
                          const staffStore = staffMember.staffStores?.find((ss: any) => ss.store?.id === storeId);
                          const isActive = staffStore?.isActive ?? true;
                          
                          return (
                            <Chip
                              label={isActive ? "Active" : "Inactive"}
                              size="small"
                              color={isActive ? "success" : "default"}
                              variant="outlined"
                              sx={{ fontWeight: 600 }}
                            />
                          );
                        })()}
                        <Chip
                          label={getRoleDisplayName(storeId ? getStaffRoleForStore(staffMember, storeId) : STAFF_ROLES.STAFF)}
                          size="small"
                          color={getRoleColor(storeId ? getStaffRoleForStore(staffMember, storeId) : STAFF_ROLES.STAFF)}
                          variant="outlined"
                          sx={{ fontWeight: 600 }}
                        />
                        {staffMember.services && staffMember.services.length > 0 && (
                          <Chip
                            label={`${staffMember.services.length} service${staffMember.services.length > 1 ? 's' : ''}`}
                            size="small"
                            color="info"
                            variant="outlined"
                            icon={<RoomService />}
                            sx={{ fontWeight: 600 }}
                          />
                        )}
                      </Box>

                      {staffMember.services && staffMember.services.length > 0 && (
                        <Box>
                          <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
                            Services:
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {staffMember.services.slice(0, 3).map((service: any) => (
                              <Chip
                                key={service.id}
                                label={service.name}
                                size="small"
                                variant="outlined"
                                sx={{ 
                                  fontSize: '0.75rem',
                                  bgcolor: 'background.default',
                                  border: 1,
                                  borderColor: 'divider'
                                }}
                              />
                            ))}
                            {staffMember.services.length > 3 && (
                              <Chip
                                label={`+${staffMember.services.length - 3}`}
                                size="small"
                                variant="outlined"
                                sx={{ 
                                  fontSize: '0.75rem',
                                  bgcolor: 'background.default',
                                  border: 1,
                                  borderColor: 'divider'
                                }}
                              />
                            )}
                          </Box>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Floating Action Button — hidden when at seat limit (header shows Buy more seats) */}
        {!atSeatLimit && (
          <Fab
            color="primary"
            aria-label="add"
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              boxShadow: theme.shadows[4],
              '&:hover': {
                boxShadow: theme.shadows[6],
              }
            }}
            onClick={() => handleOpenDialog()}
          >
            <Add />
          </Fab>
        )}
      </motion.div>

      {/* Staff Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: theme.shadows[8],
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 3, 
          pb: 1,
          borderBottom: 1,
          borderColor: 'divider'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={600}>
              {editingStaff ? 'Edit Staff Member' : 'Add New Staff Member'}
            </Typography>
            <IconButton 
              onClick={handleCloseDialog}
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  color: theme.palette.error.main,
                  bgcolor: theme.palette.error.main + '10',
                }
              }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Username"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Role</InputLabel>
                <Select
                  value={formData.role}
                  label="Role"
                  onChange={(e) => setFormData({ ...formData, role: e.target.value as StaffRole })}
                  sx={{
                    borderRadius: 1.5,
                  }}
                >
                  <MenuItem value={STAFF_ROLES.STAFF}>Staff</MenuItem>
                  <MenuItem value={STAFF_ROLES.ADMIN}>Admin</MenuItem>
                  <MenuItem value={STAFF_ROLES.OWNER}>Owner</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Title"
                placeholder="e.g. Senior Barber"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                  }
                }}
              />
            </Grid>
            {!editingStaff && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  helperText="Leave empty to auto-generate a password"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5,
                    }
                  }}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Services</InputLabel>
                <Select
                  multiple
                  value={formData.services}
                  onChange={(e) => setFormData({ ...formData, services: e.target.value as string[] })}
                  input={<OutlinedInput label="Services" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const service = services.find((s: any) => s.id === value);
                        return (
                          <Chip
                            key={value}
                            label={service?.name || value}
                            size="small"
                            sx={{
                              bgcolor: 'background.default',
                              border: 1,
                              borderColor: 'divider'
                            }}
                          />
                        );
                      })}
                    </Box>
                  )}
                  sx={{
                    borderRadius: 1.5,
                  }}
                >
                  {services.map((service: any) => (
                    <MenuItem key={service.id} value={service.id}>
                      <Checkbox checked={formData.services.includes(service.id)} />
                      <ListItemText 
                        primary={service.name} 
                        secondary={`${service.duration}min - £${service.price}`}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Staff Active Toggle */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="body1" fontWeight={600}>
                      Staff Active
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formData.isActive 
                        ? 'Staff assignment is active and available for appointments' 
                        : 'Staff assignment is inactive and excluded from appointments'
                      }
                    </Typography>
                  </Box>
                }
                sx={{ mt: 2, mb: 1 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ 
          p: 3, 
          pt: 2,
          borderTop: 1,
          borderColor: 'divider',
          gap: 2
        }}>
          <Button 
            onClick={handleCloseDialog}
            sx={{
              borderRadius: 1.5,
              px: 3,
              py: 1,
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSubmit}
            sx={{
              borderRadius: 1.5,
              px: 3,
              py: 1,
              fontWeight: 600,
              boxShadow: theme.shadows[2],
              '&:hover': {
                boxShadow: theme.shadows[4],
              }
            }}
          >
            {editingStaff ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Modal */}
      <ConfirmationModal
        open={confirmationState.open}
        onClose={closeConfirmation}
        onConfirm={handleConfirm}
        title={confirmationState.options.title}
        message={confirmationState.options.message}
        confirmText={confirmationState.options.confirmText}
        cancelText={confirmationState.options.cancelText}
        variant={confirmationState.options.variant}
        confirmButtonColor={confirmationState.options.confirmButtonColor}
      />

      {/* Alert Modal */}
      <AlertModal
        open={alertState.open}
        onClose={closeAlert}
        title={alertState.options.title}
        message={alertState.options.message}
        confirmText={alertState.options.confirmText}
        variant={alertState.options.variant}
      />

      {/* Custom Snackbar */}
      <CustomSnackbar
        snackbar={{
          open: snackbarState.open,
          message: snackbarState.message,
          severity: snackbarState.severity,
          title: snackbarState.title,
          autoHideDuration: snackbarState.autoHideDuration,
        }}
        onClose={closeSnackbar}
      />

      {/* Seat-limit modal — shown when CreateStaff/UpdateStaff returns
          SEAT_LIMIT_EXCEEDED. Routes the owner to the buy-seats page. */}
      <Dialog open={seatLimitModalOpen} onClose={() => setSeatLimitModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>You've used all your seats</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            You're using {activeSeatsUsed} of {seatCount} seats. Buy at least one
            more to add this barber. Each extra seat is £5/month and can be
            adjusted any time.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSeatLimitModalOpen(false)} sx={{ fontWeight: 600 }}>
            Not now
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setSeatLimitModalOpen(false);
              navigate(`/store/${storeId}/seats?focus=add-1`);
            }}
            sx={{ fontWeight: 600 }}
          >
            Buy seats
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Staff;
